import React, { FC, useCallback, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import clsx from 'clsx';
import useResizeWidthObserver from '../turboLive/hooks/useResizeWidthObserver';
import { ArrowSvg } from '../Arrows/Arrows';

const SwiperArrow: FC<{ side: 'left' | 'right'; onClick: () => void; enabled?: boolean }> = ({
    side,
    onClick,
    enabled
}) => {
    return (
        <button
            className={clsx('filter-arrow flex-shrink-0 filter-bgr px-10 fs-0 p-rel', {
                [`filter-arrow-${side}`]: enabled
            })}
            onClick={onClick}
            disabled={!enabled}
            aria-label={`Scroll ${side}`}
        >
            <ArrowSvg
                sizeX="16"
                sizeY="16"
                otherClassName={clsx(`arrow-${side}`, {
                    'c-black-25': !enabled,
                    'arrow-orange': enabled
                })}
            />
        </button>
    );
};

const useArrows: (
    ref,
    parentRef,
    refresh?,
    scrollDelta?: number
) => [JSX.Element, JSX.Element, (slideLeft: number) => void] = (ref, parentRef, refresh, scrollDelta) => {
    const delta = scrollDelta || 200;
    const parentWidth = useResizeWidthObserver(parentRef);
    const [slideLeft, setSlideLeft] = useState(0);
    const [arrowsVisible, setArrowsVisible] = useState(false);
    const node = ref?.current;
    useEffect(() => {
        !isMobile && node && setArrowsVisible(node.scrollWidth > node.offsetWidth);
    }, [parentWidth, node, refresh]);
    const leftArrowEnabled = slideLeft > 0;
    const rightArrowEnabled = node && node.scrollWidth - node.offsetWidth > slideLeft;
    const scrollNav = delta => {
        if (node) {
            setSlideLeft((node.scrollLeft += delta));
        }
    };
    const setScrollLeft = useCallback((slideLeft: number) => {
        setSlideLeft(slideLeft);
    }, []);
    const arrowLeft = arrowsVisible && (
        <SwiperArrow side="left" onClick={() => scrollNav(-delta)} enabled={leftArrowEnabled} />
    );
    const arrowRight = arrowsVisible && (
        <SwiperArrow side="right" onClick={() => scrollNav(delta)} enabled={rightArrowEnabled} />
    );
    return [arrowLeft, arrowRight, setScrollLeft];
};

export default useArrows;
